import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    id: 'free',
    question: '浏览站内影片需要付费或注册吗？',
    answer:
      '不需要。本站是影视门户的界面演示，全部页面均可直接浏览，不设登录墙与付费环节。站内也不会向你索取手机号、邮箱或任何支付信息。',
  },
  {
    id: 'play',
    question: '点击播放为什么没有真实的影片画面？',
    answer:
      '播放区是前端交互演示，只模拟播放、暂停、进度跳转、静音与全屏等控制行为，不会加载任何真实视频文件。页面上已标注「演示」标识，请在体验时以此为预期。',
  },
  {
    id: 'source',
    question: '站内的影片资料是从哪里来的？',
    answer:
      '全部为虚构示例：片名、简介、评分、播放量与演职人员均由项目编写，用于展示卡片、详情页与筛选用例，与任何真实作品或人物无关。',
  },
  {
    id: 'favorite',
    question: '收藏的影片会保存多久？保存在哪里？',
    answer:
      '收藏记录写入当前浏览器的本地存储，不会上传服务器。只要不清除浏览器数据、不使用无痕模式，重新打开站点仍然保留；更换设备或浏览器后不会同步。',
  },
  {
    id: 'search',
    question: '搜索支持哪些关键词？',
    answer:
      '支持影片标题、外文名、演员、导演、地区、标签以及简介文本的模糊匹配。输入越短结果越多，若没有结果，页面会给出返回首页与浏览热榜的入口。',
  },
  {
    id: 'filter',
    question: '分类页的筛选和排序可以组合使用吗？',
    answer:
      '可以。地区与年份筛选能够叠加，排序支持最近更新、点击量与评分三种方式，切换任意条件都会回到第一页。筛选后没有结果时，页面会提供一键重置按钮。',
  },
  {
    id: 'age',
    question: '为什么站点标注了 18+？',
    answer:
      '本站主题涉及成人影视内容，因此仅面向十八岁以上的成年用户。若你未满十八岁，请立即关闭页面；站内不提供任何真实视频资源。',
  },
  {
    id: 'feedback',
    question: '发现问题或有功能建议，怎么反馈？',
    answer:
      '可以前往「关于我们」页面查看演示联系邮箱。我们更关注导航结构、卡片信息密度与移动端操作体验方面的建议。',
  },
];

export default function FaqPage() {
  const [openIds, setOpenIds] = useState<string[]>([faqItems[0].id]);
  usePageMeta({
    title: '常见问题 - av免费网站',
    description:
      'av免费网站 常见问题解答：浏览是否需要付费、播放演示说明、收藏保存位置、搜索与筛选用法、内容年龄限制等。',
    keywords: 'av免费网站常见问题,播放演示,收藏说明,AV在线观看,高清影视',
  });

  const toggle = (id: string) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <div>
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-10 md:pb-14">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-800">常见问题</span>
          </nav>
          <div className="mt-7 max-w-2xl">
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">FAQ</p>
            <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-tight">常见问题</h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              关于浏览、播放演示、收藏保存与内容声明的高频问题。点击问题即可展开答案，可以同时展开多条。
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 md:gap-8 items-start">
          <div className="space-y-3">
            {faqItems.map((item, index) => {
              const open = openIds.includes(item.id);
              return (
                <div
                  key={item.id}
                  className={`rounded-lg border transition-colors ${
                    open
                      ? 'border-primary-500/40 bg-background-100'
                      : 'border-background-300/60 bg-background-100 hover:border-background-400/70'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="flex items-start gap-3 min-w-0">
                      <span className="font-label text-sm text-foreground-500 shrink-0 mt-0.5">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-sm md:text-[15px] font-medium text-foreground-950">
                        {item.question}
                      </span>
                    </span>
                    <span
                      className={`w-8 h-8 shrink-0 rounded-md border flex items-center justify-center transition-colors ${
                        open
                          ? 'border-primary-500/50 bg-primary-500/15 text-primary-400'
                          : 'border-background-300/70 text-foreground-600'
                      }`}
                    >
                      <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5 animate-fade-in">
                      <p className="pl-0 md:pl-8 text-sm leading-relaxed text-foreground-700">{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-300/60 bg-secondary-50 p-5">
              <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-lg w-5 h-5 flex items-center justify-center" />
              </span>
              <h2 className="mt-3 text-base font-bold">没有找到答案？</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                关于定位、免责声明与联系方式，可以在「关于我们」页面查看完整说明。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center justify-center gap-2 w-full h-10 rounded-md border border-secondary-300/50 text-sm text-secondary-900 hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往关于我们
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>

            <div className="rounded-lg border border-background-300/60 bg-background-100 p-5">
              <p className="text-[11px] tracking-[0.24em] text-secondary-600 font-label uppercase">Start Here</p>
              <h2 className="mt-2 text-base font-bold">直接开始浏览</h2>
              <div className="mt-3 space-y-2">
                {[
                  { label: '最新上架', to: '/category/latest' },
                  { label: '热门榜单', to: '/category/hot' },
                  { label: '经典修复', to: '/category/classic' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center justify-between rounded-md bg-background-50 border border-background-300/60 px-3.5 py-2.5 text-sm text-foreground-800 hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer"
                  >
                    {link.label}
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}